// src/components/sections/DemoSection.tsx
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Edit2, Package, PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react';

import {
  AgentData_V10,
  AgentHubPageViewMode,
  DemoSectionPublicV10,
  VisualElementV10,
} from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { VisualElement } from '@dopomogai/ui';

const getDemoType = (type: VisualElementV10['type'], t: Function): string => {
  switch (type) {
    case 'video_url': return t('demoSection.demoType_video', 'Video');
    case 'image': return t('demoSection.demoType_image', 'Image');
    case '3d_model_url': return t('demoSection.demoType_3d', '3D Model');
    case 'interactive_animation_url': return t('demoSection.demoType_interactive', 'Interactive');
    default: return t('demoSection.demoType_image', 'Image');
  }
};

interface DemoSectionProps {
  agentData?: AgentData_V10;
  data?: DemoSectionPublicV10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

export const DemoSection = React.forwardRef<HTMLDivElement, DemoSectionProps>(
  ({ agentData, data, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';
    const sectionData = data || agentData?.demoSectionPublic;
    const demoElements = sectionData?.demoElements || [];
    const [activeIndex, setActiveIndex] = useState(0);

    const activeDemo = useMemo(() => {
      if (demoElements.length > 0) {
        return demoElements[activeIndex];
      }
      return null;
    }, [demoElements, activeIndex]);

    if (!sectionData || demoElements.length === 0) {
      if (isOwnerView) {
        return (
          <Card id={id} ref={ref} titleKey="demoSection.defaultTitle" icon={<Package size={22} />} headerBgColor={headerBgColor}>
            <div className="text-center py-10">
              <p className="text-gray-500 mb-4">{t('demoSection.noDemoContent')}</p>
              <Button textKey="demoSection.addDemoContentButton" onClick={() => onAction('MANAGE_DEMOS', null)} />
            </div>
          </Card>
        );
      }
      return null;
    }
    
    const handleNext = () => setActiveIndex((prev) => (prev + 1) % demoElements.length);
    const handlePrev = () => setActiveIndex((prev) => (prev - 1 + demoElements.length) % demoElements.length);
    const mainViewerVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } }, exit: { opacity: 0, scale: 1.02, transition: { duration: 0.2, ease: 'easeIn' } }, };

    // --- FIX: Check for the 'caption' property before trying to access it. ---
    const getAriaLabel = (demo: VisualElementV10) => {
        const captionOrType = 'caption' in demo ? demo.caption : getDemoType(demo.type, t);
        return t('demoSection.selectDemoAriaLabel', { captionOrType });
    };

    return (
      <Card
        id={id} ref={ref}
        title={sectionData.title || t('demoSection.defaultTitle', 'Demo Showcase')}
        headerBgColor={headerBgColor}
        icon={<PlayCircle size={22} className="text-rose-600" />}
        actionButton={isOwnerView ? (<Button textKey="demoSection.manageDemosButton" variant="outline" size="sm" icon={<Edit2 size={14} />} onClick={() => onAction('MANAGE_DEMOS', null)}/>) : undefined}
      >
        <div className="flex flex-col items-center">
          <div className="w-full max-w-4xl relative">
            <AnimatePresence initial={false} mode="wait">
              {activeDemo && (
                <motion.div key={activeIndex} variants={mainViewerVariants} initial="hidden" animate="visible" exit="exit" className="w-full bg-black rounded-xl border-2 border-black overflow-hidden shadow-[4px_4px_0_0_rgb(17,24,39)]">
                  <VisualElement visual={activeDemo} className="w-full aspect-video" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="w-full max-w-3xl text-center mt-4 h-12">
            <AnimatePresence initial={false} mode="wait">
              {/* --- FIX: Check if 'caption' exists on the activeDemo object --- */}
              {activeDemo && 'caption' in activeDemo && (
                <motion.p key={activeIndex} variants={mainViewerVariants} initial="hidden" animate="visible" exit="exit" className="text-gray-700 text-sm md:text-base leading-relaxed">
                  {activeDemo.caption}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
          {demoElements.length > 1 && (
            <div className="w-full max-w-3xl flex items-center gap-2 mt-4">
              <button onClick={handlePrev} aria-label={t('demoSection.previousDemoAriaLabel', 'Previous demo')} className="p-2 rounded-full border-2 border-black bg-white hover:bg-slate-100 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft size={20} />
              </button>
              <div className="flex-grow overflow-x-auto scrollbar-hide">
                <div className="flex items-center justify-start sm:justify-center gap-3 px-2">
                  {demoElements.map((demo, index) => (
                    <button
                      // --- FIX: Check if 'url' exists before using it for the key. ---
                      key={'url' in demo ? demo.url : index}
                      onClick={() => setActiveIndex(index)}
                      aria-label={getAriaLabel(demo)}
                      className={clsx("relative w-20 h-14 sm:w-24 sm:h-16 rounded-lg border-2 overflow-hidden flex-shrink-0 transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-rose-500", activeIndex === index ? 'border-rose-500 opacity-100 scale-105 shadow-lg' : 'border-black opacity-70 hover:opacity-100')}
                    >
                      <VisualElement visual={demo} className="w-full h-full object-cover" isThumbnail={true} />
                      {demo.type === 'video_url' && (<div className="absolute inset-0 bg-black/20 flex items-center justify-center"><PlayCircle size={24} className="text-white/80" /></div>)}
                      <div className={clsx("absolute inset-0 border-4 transition-colors duration-200", activeIndex === index ? 'border-rose-500' : 'border-transparent')}></div>
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={handleNext} aria-label={t('demoSection.nextDemoAriaLabel', 'Next demo')} className="p-2 rounded-full border-2 border-black bg-white hover:bg-slate-100 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </Card>
    );
  }
);
DemoSection.displayName = "DemoSection";